import React from 'react';
import { Descriptions, Drawer, Image, Space, Tag, Typography } from 'antd';
import { getAddressNames } from '../../../utils/addressUtils';

interface ExternalProductDetailDrawerProps {
  open: boolean;
  loading: boolean;
  detail: any;
  onClose: () => void;
}

const imageFallback = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADwAAAA8CAYAAAA6/NlyAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAEoSURBVHgB7doxDoJAFIThX2gsKaxsOIOn4AgWHMGCI9h4Ak/gCTiBJ+AE3Xc0JhYKhYSF3W+S/SUvYGd3duYfAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAv4RSCpBSwjknsZbLJeZ5xjAMGGOgtdZo2xbGGNy3ner6vo21VnRdJ601+r7HPM/oupBS4nQ6oaoqzPN8j/d93yKu6xrv92ut0XUdzufz/R5rLcYY4pzjPM/4vvme/U/btqh1XZPLZYLWev2FEELYR7TWUtd15nme6/2u65I554/jOI5SSuKcs4g45yullKSUxFrrJMuytK7rMgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAPiUH8OMPVUxpmkKAAAAAElFTkSuQmCC';

const renderDetailField = (label: string, value: any) => (
  <Descriptions.Item label={label} span={3} style={{ wordBreak: 'break-all' }}>
    {value === '' || value === null || value === undefined ? '-' : String(value)}
  </Descriptions.Item>
);

const renderImageField = (label: string, url: string) => {
  const isValid = url && typeof url === 'string' && url.startsWith('http');
  return (
    <Descriptions.Item label={label} span={3}>
      {isValid ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1, wordBreak: 'break-all', fontSize: 12, color: '#1677ff' }}>{url}</span>
          <Image
            src={url}
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
            preview={{ mask: '点击放大' }}
            fallback={imageFallback}
          />
        </div>
      ) : (
        <span>{url || '-'}</span>
      )}
    </Descriptions.Item>
  );
};

const ExternalProductDetailDrawer: React.FC<ExternalProductDetailDrawerProps> = ({
  open,
  loading,
  detail,
  onClose,
}) => {
  return (
    <Drawer
      title={
        <Space>
          <span>外部产品详情</span>
          {detail && (
            <Tag color="blue">ID: {detail.p_id}</Tag>
          )}
        </Space>
      }
      placement="right"
      width={720}
      onClose={onClose}
      open={open}
      loading={loading}
    >
      {detail && (
        <Image.PreviewGroup>
          <Typography.Title level={5}>基本信息</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            {renderDetailField('产品ID', detail.p_id)}
            {renderDetailField('产品名称', detail.p_goods_name)}
            {renderImageField('产品图片', detail.p_goods_image)}
            {renderDetailField('运营商',
              detail.operator === 1 ? '移动' :
              detail.operator === 2 ? '联通' :
              detail.operator === 3 ? '电信' : '未知')}
            {renderDetailField('是否移动超卡', detail.is_ydchama === '1' ? '是' : '否')}
            {renderDetailField('是否自动识别运营商', detail.is_automatic_recognition === '1' ? '是' : '否')}
            {renderDetailField('产品面值/价格', detail.p_goods_price ? `${detail.p_goods_price}元` : '-')}
            {renderDetailField('提交类型',
              detail.p_submit_type === '1' ? '普通提交' :
              detail.p_submit_type === '2' ? '需审核' : detail.p_submit_type)}
            {renderDetailField('是否卡类产品', detail.is_card ? '是' : '否')}
            {renderDetailField('所属代理商ID', detail.p_agent_id)}
            {renderDetailField('创建者ID', detail.p_create_id)}
            {renderDetailField('加密代理商ID', detail.en_agent_id)}
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: 16 }}>套餐配置（微店）</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="当前套餐流量" span={1}>{detail.micro_store?.current || '-'}G</Descriptions.Item>
            <Descriptions.Item label="定向流量" span={1}>{detail.micro_store?.directional || '-'}G</Descriptions.Item>
            <Descriptions.Item label="语音通话" span={1}>{detail.micro_store?.telephone || '-'}分钟</Descriptions.Item>
            <Descriptions.Item label="原月租" span={1}>{detail.micro_store?.original_monthly || '-'}元</Descriptions.Item>
            <Descriptions.Item label="当前月租" span={1}>{detail.micro_store?.current_monthly || '-'}元</Descriptions.Item>
            <Descriptions.Item label="充值说明" span={2}>{detail.micro_store?.charge_description || '-'}</Descriptions.Item>
            <Descriptions.Item label="商品副标题" span={2}>{detail.micro_store?.goods_subtitle || '-'}</Descriptions.Item>
            <Descriptions.Item label="微店排序值" span={1}>{detail.micro_store_sort ?? '-'}</Descriptions.Item>
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: 16 }}>结算信息</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            {renderDetailField('结算说明', detail.settle_accounts_msg)}
            {renderDetailField('结算说明(简)', detail.settle_accounts_msg_str)}
            {renderDetailField('付款金额', detail.pay_money ? `${detail.pay_money}元` : '-')}
            {renderDetailField('是否需付款', detail.is_pay_money ? '需要' : '不需要')}
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: 16 }}>发货/配送信息</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="发货地址类型" span={2}>
              {detail.p_address_type === 'NonShippingRegion' ? (
                <Tag color="red">不发货区域(黑名单) - 以下区域不发货，其他区域均可发</Tag>
              ) : detail.p_address_type === 'ShippingRegionOnly' ? (
                <Tag color="green">仅发货区域(白名单) - 仅以下区域可发货</Tag>
              ) : (
                <Tag>{detail.p_address_type || '-'}</Tag>
              )}
            </Descriptions.Item>
            {detail.p_address_id && detail.p_address_id.length > 0 && (
              <Descriptions.Item label="关联省份" span={2}>
                <Space wrap>
                  {getAddressNames(detail.p_address_id.map(Number)).map((name, index) => (
                    <Tag key={index} color={detail.p_address_type === 'ShippingRegionOnly' ? 'green' : 'red'}>{name}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            {detail.address_id_city && typeof detail.address_id_city === 'string' && detail.address_id_city.length > 0 && (
              <Descriptions.Item label="关联城市(字符串)" span={2}>
                <Tag>{detail.address_id_city}</Tag>
              </Descriptions.Item>
            )}
            {detail.address_id_city && Array.isArray(detail.address_id_city) && detail.address_id_city.length > 0 && (
              <Descriptions.Item label="关联城市" span={2}>
                <Space wrap>
                  {getAddressNames(detail.address_id_city.map(Number)).map((name, index) => (
                    <Tag key={index}>{name}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            {renderDetailField('城市地址ID', detail.address_id_city)}
            {renderDetailField('是否需要收货地址', detail.is_place ? '是' : '否')}
            {renderDetailField('是否使用发货地址库', detail.p_select_deliver_address_library ? '是' : '否')}
            {renderDetailField('限购信息', detail.p_limit || '不限购')}
            {renderDetailField('禁用城市', detail.disable_city || '无')}
            {renderDetailField('禁用城市说明', detail.disable_city_msg || '无')}
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: 16 }}>选号配置</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            {renderDetailField('是否支持选号', detail.p_pick_type ? '支持' : '不支持')}
            {renderDetailField('归属地', detail.p_pick_set?.gsd || '-')}
            {renderDetailField('可选地址库', detail.p_pick_set?.p_select_address_library || '-')}
            {renderDetailField('收货地址', detail.p_pick_set?.place_address || '-')}
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: 16 }}>身份证要求</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            {renderDetailField('是否需要身份证', detail.p_card?.is_idcard ? '需要' : '不需要')}
            {renderDetailField('是否需要身份证图片', detail.p_card?.idcard_pic ? '需要' : '不需要')}
            {renderDetailField('身份证图片数量', detail.p_card?.idcard_pic_num)}
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: 16 }}>二维码/跳转</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            {renderImageField('二维码1(充值码)', detail.goods_qrcode)}
            {renderImageField('二维码2(查询码)', detail.goods_qrcode2)}
            {renderImageField('二维码3(通用码)', detail.goods_qrcode3)}
            {renderImageField('二维码5(通用码2)', detail.goods_qrcode5)}
            {renderDetailField('跳转名称', detail.jump_url?.jump_to_name === '0' ? '不跳转' : detail.jump_url?.jump_to_name)}
            {renderDetailField('跳转URL', detail.jump_url?.jump_to_url || '-')}
            {renderDetailField('复制跳转路径', detail.copy_jump)}
            {renderImageField('分享图片', detail.share_picture)}
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: 16 }}>模板配置</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            {renderDetailField('模板备注', detail.p_template?.note_)}
            {renderImageField('按钮图片', detail.p_template?.button_img)}
            {renderDetailField('按钮类型', detail.p_template?.button_type)}
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: 16 }}>其他信息</Typography.Title>
          <Descriptions bordered size="small" column={2}>
            {renderDetailField('是否置亮', detail.p_is_lamp ? '亮' : '不亮')}
            {renderDetailField('是否微信支付', detail.p_is_weixin ? '是' : '否')}
            {renderDetailField('金额状态', detail.p_money_status === 0 ? '未设置' : detail.p_money_status)}
            {renderDetailField('金额', detail.p_money ? `${detail.p_money}元` : '-')}
            {renderDetailField('是否需要手机验证码', detail.p_moblie_code ? '需要' : '不需要')}
            {renderDetailField('分组ID', detail.gid || '-')}
          </Descriptions>

          {detail.details && (
            <>
              <Typography.Title level={5} style={{ marginTop: 16 }}>商品详情（HTML）</Typography.Title>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: 16,
                  maxHeight: 400,
                  overflow: 'auto',
                  background: '#fafafa',
                }}
                dangerouslySetInnerHTML={{ __html: detail.details }}
              />
            </>
          )}
        </Image.PreviewGroup>
      )}
    </Drawer>
  );
};

export default ExternalProductDetailDrawer;
